import React from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { variants, VIEWPORT } from '../../lib/motion-presets';

/**
 * <Reveal> — scroll-triggered entrance for any block.
 *
 * Props:
 *  as        — element/component to render (default 'div'); must accept className/style/children
 *  variant   — one of: 'fadeUp' | 'fadeIn' | 'scaleIn' | 'slideLeft' | 'slideRight' (default 'fadeUp')
 *  delay     — seconds to delay the entrance (default 0)
 *  duration  — override duration in seconds (optional)
 *  once      — animate once on first enter (default true)
 *  amount    — fraction of element that must be visible (default 0.2)
 *  className  — passthrough
 *  style      — passthrough
 *  children   — passthrough
 */
const VARIANT_MAP = {
  fadeUp: variants.fadeUp,
  fadeIn: variants.fadeIn,
  scaleIn: variants.scaleIn,
  slideLeft: variants.slideLeft,
  slideRight: variants.slideRight,
};

const Reveal = ({
  as = 'div',
  variant = 'fadeUp',
  delay = 0,
  duration,
  once = true,
  amount = 0.2,
  className = '',
  style,
  children,
  ...rest
}) => {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;
  const v = VARIANT_MAP[variant] || variants.fadeUp;

  // When the user prefers reduced motion, snap to visible with no transform.
  const resolved = reduce
    ? { hidden: { opacity: 1 }, visible: { opacity: 1, transition: { duration: 0 } } }
    : {
        hidden: v.hidden,
        visible: {
          ...v.visible,
          transition: duration
            ? { duration, ease: v.visible.transition.ease, delay }
            : { ...v.visible.transition, delay },
        },
      };

  return (
    <MotionTag
      className={className}
      style={style}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount, margin: VIEWPORT.margin }}
      variants={resolved}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

export default Reveal;
